import {
  LoadingIndicator,
  Typography,
} from "@anchor/react-components/dist/lib/components";
import { McButton } from "@maersk-global/mds-react-wrapper/components-core";
import { McTable } from "@maersk-global/mds-react-wrapper/components-core/mc-table";
import React, { useEffect, useState, useMemo } from "react";
import JiraModal from "./JiraModal";
import { Col, Row } from "react-bootstrap";
import UseHandleFileUpload from "../../Utils/UseHandleFileUpload";
import Images from "../Images/Images";
import PhotoUploadModal from "../PhotoUploadModal/PhotoUploadModal";
import { useMsal } from "@azure/msal-react";
import UploadedImages from "../Images/UploadedImages";
import { FiRefreshCcw } from "react-icons/fi";
import { useCallback } from "react";
import { ImageServices } from "../../services/imageServices";
import JiraCreateModal from "./JiraCreateModal";
import UserInfo from "../../context/UserAccessContext";
import { IsLocked } from "../IsLocked/IsLocked";

const JIRA_BASE_URL = "https://maersk-tools.atlassian.net/browse/";

const TAG_BASE =
  "display:inline-block;padding:2px 10px;border-radius:10px;font-size:12px;font-weight:600;white-space:nowrap;line-height:16px;";

const TAG_STYLES = {
  success: `${TAG_BASE}background-color:var(--mds_brand_appearance_success_weak_background-color,#d4edda);color:var(--mds_brand_appearance_success_weak_on-background-color,#155724);`,
  error: `${TAG_BASE}background-color:var(--mds_brand_appearance_error_weak_background-color,#fcdde0);color:var(--mds_brand_appearance_error_weak_on-background-color,#370005);`,
  warning: `${TAG_BASE}background-color:var(--mds_brand_appearance_warning_weak_background-color,#fff3cd);color:var(--mds_brand_appearance_warning_weak_on-background-color,#856404);`,
  neutral: `${TAG_BASE}background-color:var(--mds_brand_appearance_neutral_weakest_background-color,#f7f7f7);color:var(--mds_brand_appearance_neutral_weakest_on-background-color,#616161);`,
};

const JIRA_COLUMNS = [
  {
    id: "jiraid",
    label: "Jira ID",
    noWrap: true,
    cellTemplate: (e) =>
      e.html`<a href="${JIRA_BASE_URL}${e.rowData.jiraid}" target="_blank" rel="noopener noreferrer" style="color:var(--mds_brand_appearance_primary_default_text-color,#0073ab);text-decoration:none;">${e.rowData.jiraid}</a>`,
  },
  { id: "description", label: "Summary" },
  { id: "assignee", label: "Assigned To" },
  { id: "platformPortfolio", label: "Portfolio" },
  { id: "jiraPlatform", label: "Platform" },
  { id: "jiraProduct", label: "Product" },
  { id: "horizontalVerticalFix", label: "Fix Type" },
  {
    id: "status",
    label: "Status",
    noWrap: true,
    cellTemplate: (e) =>
      e.html`<span style="${TAG_STYLES[e.rowData.statusAppearance] || TAG_STYLES.neutral}">${e.rowData.status}</span>`,
  },
  { id: "duedate", label: "Due Date", noWrap: true },
];

function getStatusAppearance(status, duedate) {
  const s = status?.toLowerCase();
  if (s === "done" || s === "closed") return "success";
  if (duedate) {
    const due = new Date(duedate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (due < today) return "error";
  }
  return "warning";
}

const Loading = () => (
  <LoadingIndicator
    appearance="default"
    fit="large"
    id="testing"
    label="Fetching images, Please Wait..."
    variant="spinner"
    style={{ background: "none" }}
  />
);

function Jira({
  contextState,
  jiraTickets,
  dispatch,
  images,
  imageFormData,
  incidentID,
  currentStep,
  lockedFields,
  changedFields,
  onAutoSave,
}) {
  const lockField = null;
  const unlockField = null;
  const { graphData } = UserInfo();

  const handleFieldLocking = () => { };

  const [uploadedJiraImages, setUploadedJiraImages] = useState([]);
  const { instance, accounts } = useMsal();
  const [jiraModal, setJiraModal] = useState(false);
  const [currentJiraTickets, setCurrentJiraTickets] = useState(jiraTickets);
  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [fetchAgain, setFetchAgain] = useState(false);
  const [photosFor, setPhotosFor] = useState("jira");
  const [photoModal, setPhotoModal] = useState(false);
  const [imageToPreview, setImageToPreview] = useState({ preview: "", fileName: "" });
  const [previewModal, setPreviewModal] = useState(false);
  const [loadingImage, setLoadingImage] = useState(false);
  const [errorInFetchingImages, setErrorInFetchingImages] = useState(false);
  const [jiraCreateModal, setJiraCreateModal] = useState(false);

  const handleJiraCreateModal = () => {
    if (!jiraCreateModal) handleFieldLocking("jira");
    setJiraCreateModal(!jiraCreateModal);
  };

  const { getImage } = ImageServices();

  const handleModal = () => {
    setJiraModal(true);
    handleFieldLocking("jira");
  };

  const closeModal = () => setJiraModal(false);

  useEffect(() => {
    if (!jiraModal) setCurrentJiraTickets(jiraTickets);
  }, [jiraModal, jiraTickets]);

  const fetchAndSetImages = useCallback(
    async (setterFunction, tag) => {
      setLoadingImage(true);
      try {
        const imgs = await getImage([], incidentID, tag);
        setterFunction(imgs);
        setErrorInFetchingImages(false);
      } catch (err) {
        setErrorInFetchingImages(true);
      } finally {
        setLoadingImage(false);
      }
    },
    [incidentID, instance, accounts]
  );

  useEffect(() => {
    if (incidentID && currentStep === 2)
      fetchAndSetImages(setUploadedJiraImages, "jira");
  }, [incidentID, fetchAgain, fetchAndSetImages, currentStep]);

  const onSave = (tickets) => {
    const newJiraTickets = [...tickets];
    const hasChanged = JSON.stringify(tickets) !== JSON.stringify(jiraTickets);
    dispatch({
      type: "HANDLE EDITOR CHANGE",
      field: "PreventiveAndCorrectiveMeasuresState",
      payload: { ...contextState, jiraTickets: newJiraTickets },
    });
    dispatch({
      type: "HANDLE EDITOR CHANGE",
      field: "changedFileds",
      payload: (prevChangedFileds) => ({
        ...prevChangedFileds,
        jiraTickets: hasChanged ? true : prevChangedFileds.jiraTickets || false,
      }),
    });
    closeModal();
    if (onAutoSave) setTimeout(() => { onAutoSave(); }, 100);
  };

  const hanldePreviewModal = () => setPreviewModal(!previewModal);
  const handlePhotoModalClick = (fotoFor) => { setPhotosFor(fotoFor); setPhotoModal(true); };
  const handlePhotoModalClose = () => setPhotoModal(false);

  const { handleFiles, fileChange, deleteImage, previewImage } = UseHandleFileUpload(
    images, setError, setErrorMessage, photosFor, hanldePreviewModal,
    setImageToPreview, dispatch, imageFormData, "preventiveCorrectiveMeasures",
    setFetchAgain, incidentID
  );

  const tableData = useMemo(
    () =>
      (jiraTickets || []).map((ticket, index) => ({
        id: index,
        jiraid: ticket.jiraid[0],
        description: ticket.description || "",
        assignee: ticket.assignee || "",
        platformPortfolio: ticket.platformPortfolio || "",
        jiraPlatform: ticket.jiraPlatform || "-",
        jiraProduct: ticket.jiraProduct || "-",
        horizontalVerticalFix: ticket.horizontalVerticalFix || "-",
        status: ticket.status || "-",
        statusAppearance: getStatusAppearance(ticket.status, ticket.duedate),
        duedate: ticket.duedate || "",
      })),
    [jiraTickets]
  );

  return (
    <>
      <div>
        <div style={{ marginLeft: "10px" }}>
          <div style={{ display: "flex", flexDirection: "column", width: "minContent" }}>
            <div style={{ display: "flex", flexDirection: "row", gap: "8px" }}>
              <McButton
                style={{ padding: "5px", fontSize: "6px" }}
                click={handleJiraCreateModal}
                className="add_ticket jira-ticket-add-tour-step"
                disabled={lockedFields.find(
                  (field) => field.fieldName === "jira" && field.lockedBy !== graphData.username
                )}
              >
                Create Ticket
              </McButton>
              <McButton
                style={{ padding: "5px", fontSize: "6px" }}
                click={handleModal}
                className="add_ticket jira-ticket-add-tour-step"
                disabled={lockedFields.find(
                  (field) => field.fieldName === "jira" && field.lockedBy !== graphData.username
                )}
              >
                Add/Edit Ticket
              </McButton>
            </div>
            <IsLocked field="jira" lockedFields={lockedFields} graphData={graphData} />
          </div>
        </div>

        {jiraTickets && jiraTickets.length > 0 ? (
          <div style={{ marginTop: "16px" }}>
            <McTable data={tableData} columns={JIRA_COLUMNS} fit="small" caption="Jira Tickets" />
          </div>
        ) : (
          <div className="jira-tickets-table-tour-step" style={{ display: "flex", alignItems: "center", justifyContent: "center", marginTop: "50px" }}>
            <Typography variant="h4" style={{ textAlign: "center", color: "grey" }}>
              No Jira Tickets Added
            </Typography>
          </div>
        )}
      </div>

      <JiraModal
        jiraTickets={jiraTickets}
        handleModal={handleModal}
        modalOpen={jiraModal}
        onSave={onSave}
        currentJiraTickets={currentJiraTickets}
        setCurrentJiraTickets={setCurrentJiraTickets}
      />
      <JiraCreateModal
        handleModal={handleJiraCreateModal}
        modalOpen={jiraCreateModal}
        handleJiraStoreModal={handleModal}
        setCurrentJiraTickets={setCurrentJiraTickets}
        currentJiraTickets={currentJiraTickets}
        saveInContenxt={onSave}
      />
      <PhotoUploadModal
        modalOpen={photoModal}
        handleModal={handlePhotoModalClick}
        handleModalClose={handlePhotoModalClose}
        files={images.preventiveCorrectiveMeasures[photosFor]}
        handleFiles={handleFiles}
        fileChange={fileChange}
        hanldePreviewModal={hanldePreviewModal}
        imageToPreview={imageToPreview}
        previewModal={previewModal}
        error={error}
        errorMessage={errorMessage}
        photoFor={photosFor}
      />
    </>
  );
}

export default Jira;
